import type { Locale } from "@/config/experience";

export type NewsCategoryId = "tu-van" | "so-sanh" | "du-an-thi-cong" | "cong-nghe";

export interface NewsSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface NewsArticleCopy {
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  intro: string;
  sections: NewsSection[];
  conclusion: string;
}

export interface NewsArticle {
  slug: string;
  category: NewsCategoryId;
  publishedAt: string;
  updatedAt: string;
  readMinutes: number;
  relatedPaths: string[];
  vi: NewsArticleCopy;
  en: NewsArticleCopy;
}

export const newsCategories = [
  {
    "id": "tu-van",
    "vi": "Tư vấn",
    "en": "Advice"
  },
  {
    "id": "so-sanh",
    "vi": "So sánh sản phẩm",
    "en": "Product comparisons"
  },
  {
    "id": "du-an-thi-cong",
    "vi": "Dự án & thi công",
    "en": "Projects & installation"
  },
  {
    "id": "cong-nghe",
    "vi": "Công nghệ",
    "en": "Technology"
  }
] as const;

export const newsArticles: NewsArticle[] = [
  {
    "slug": "khoa-can-ho-nen-chon-face-id-hay-van-tay",
    "category": "tu-van",
    "publishedAt": "2026-10-04",
    "updatedAt": "2026-10-04",
    "readMinutes": 6,
    "relatedPaths": [
      "/khoa-thong-minh/nhom/can-ho",
      "/khoa-thong-minh-da-nang"
    ],
    "vi": {
      "title": "Khóa căn hộ nên chọn Face ID hay vân tay? Checklist trước khi lắp",
      "metaTitle": "Khóa căn hộ: Face ID hay vân tay? | T Smart",
      "metaDescription": "Cách chọn khóa căn hộ theo Face ID, vân tay, loại cửa, độ dày và thói quen sử dụng. Checklist cần kiểm tra trước khi lắp khóa thông minh.",
      "excerpt": "Face ID và vân tay đều tiện, nhưng loại cửa, kết cấu đố và cách gia đình sử dụng mới là yếu tố nên kiểm tra trước khi chốt khóa căn hộ.",
      "intro": "Với căn hộ, câu hỏi thường gặp nhất là nên chọn Face ID hay vân tay. Thực tế, công nghệ mở khóa chỉ là một phần. Trước khi so sánh tính năng, cần xác định loại cửa, độ dày, chiều rộng đố, thân khóa hiện tại và nhu cầu của từng thành viên.",
      "sections": [
        {
          "heading": "1. Bắt đầu từ loại cửa, không bắt đầu từ tính năng",
          "paragraphs": [
            "Cùng một mẫu khóa có thể phù hợp với cửa này nhưng không phù hợp với cửa khác. Với cửa gỗ, cửa an ninh, cửa đồng hoặc cửa nhôm, kích thước đố và thân khóa là dữ liệu cần đối chiếu trước.",
            "Nếu chỉ chọn theo quảng cáo Face ID hoặc số lượng phương thức mở, bạn có thể gặp tình trạng phải chỉnh sửa cửa nhiều hơn dự kiến hoặc không đạt thẩm mỹ mong muốn."
          ],
          "bullets": [
            "Chụp rõ mặt trong và mặt ngoài cửa.",
            "Đo độ dày cánh và chiều rộng đố.",
            "Ghi nhận hướng mở và vị trí tay nắm.",
            "Nếu có thể, chụp cạnh cửa để thấy thân khóa hiện tại."
          ]
        },
        {
          "heading": "2. Face ID phù hợp khi nào?",
          "paragraphs": [
            "Face ID phù hợp với người muốn mở cửa không chạm và thao tác nhanh khi tay đang bận. Đây cũng là lựa chọn dễ dùng với gia đình có nhiều thành viên nếu model hỗ trợ nhận diện ổn định trong điều kiện lắp đặt thực tế.",
            "Tuy nhiên, nên kiểm tra vị trí lắp, chiều cao người dùng và góc nhận diện thay vì mặc định mọi cửa đều cho trải nghiệm giống nhau."
          ]
        },
        {
          "heading": "3. Vân tay vẫn là lựa chọn rất thực dụng",
          "paragraphs": [
            "Vân tay có ưu điểm là quen thuộc, thao tác trực tiếp và xuất hiện trên nhiều phân khúc sản phẩm. Với gia đình ưu tiên chi phí hợp lý và cách dùng đơn giản, vân tay thường là điểm khởi đầu tốt.",
            "Nên kiểm tra vị trí cảm biến, cách đặt ngón tay và khả năng đăng ký nhiều người dùng. Trẻ nhỏ hoặc người có vân tay mờ nên có thêm PIN, thẻ hoặc phương thức dự phòng phù hợp."
          ]
        },
        {
          "heading": "4. Khi nào nên chọn khóa có cả Face ID và vân tay?",
          "paragraphs": [
            "Nếu ngân sách cho phép và cửa tương thích, kết hợp Face ID cùng vân tay giúp gia đình có nhiều phương án mở. Mục tiêu không phải có càng nhiều tính năng càng tốt, mà là tạo dự phòng hợp lý khi một phương thức không thuận tiện.",
            "Hãy ưu tiên model có thông số rõ ràng, nguồn điện và cách cấp nguồn khẩn cấp dễ hiểu, đồng thời kiểm tra chính sách lắp đặt và hậu mãi trước khi mua."
          ]
        },
        {
          "heading": "5. Checklist cuối trước khi đặt hàng",
          "paragraphs": [
            "Trước khi chốt model, hãy đối chiếu lại loại cửa, kích thước, phương thức mở mong muốn, kết nối ứng dụng nếu cần và vị trí lắp. Với cửa đặc thù, nên khảo sát trước thi công."
          ],
          "bullets": [
            "Loại cửa và kích thước đã xác minh.",
            "Phương thức mở chính và dự phòng rõ ràng.",
            "Có phương án cấp nguồn khi pin yếu.",
            "Giá và biến thể đúng model.",
            "Đã thống nhất mức độ can thiệp lên cửa."
          ]
        }
      ],
      "conclusion": "Nếu chưa chắc model nào phù hợp, hãy bắt đầu từ nhóm Khóa căn hộ và gửi hình cửa để đối chiếu trước. Cách này hiệu quả hơn việc chọn khóa chỉ dựa vào tính năng nổi bật."
    },
    "en": {
      "title": "Face ID or fingerprint for an apartment smart lock? A pre-install checklist",
      "metaTitle": "Apartment smart locks: Face ID or fingerprint? | T Smart",
      "metaDescription": "Choose an apartment smart lock by Face ID, fingerprint, door construction, thickness and daily use. Review the pre-installation checklist.",
      "excerpt": "Face ID and fingerprint are both convenient, but door construction and how the household uses the lock should be checked first.",
      "intro": "For apartment doors, the common question is Face ID versus fingerprint. In practice, the unlocking technology is only one part of the decision. Door type, thickness, stile width, mortise and household use should be confirmed first.",
      "sections": [
        {
          "heading": "1. Start with the door, not the feature list",
          "paragraphs": [
            "A lock can fit one door and be unsuitable for another. Wood, security, copper and aluminium doors can have very different fitting constraints.",
            "Choosing only by headline features can lead to unnecessary modifications or a poor visual result."
          ],
          "bullets": [
            "Photograph both sides of the door.",
            "Measure door thickness and stile width.",
            "Confirm opening direction and handle position.",
            "Photograph the door edge and existing mortise when possible."
          ]
        },
        {
          "heading": "2. When does Face ID make sense?",
          "paragraphs": [
            "Face ID suits users who want touch-free access and quick entry when their hands are occupied. It can also be convenient for households with several users.",
            "Check installation height, viewing angle and the actual door location instead of assuming every installation behaves the same."
          ]
        },
        {
          "heading": "3. Fingerprint remains a practical choice",
          "paragraphs": [
            "Fingerprint access is familiar, direct and widely available across product ranges. It is often a sensible starting point when simplicity and budget matter.",
            "Check sensor placement and support for multiple users. PIN, card or another backup method can be useful for children or users with difficult fingerprints."
          ]
        },
        {
          "heading": "4. Combining Face ID and fingerprint",
          "paragraphs": [
            "When budget and door compatibility allow, combining Face ID and fingerprint can provide useful redundancy. The goal is not to maximise features, but to create reliable access options.",
            "Prefer models with clear specifications, understandable emergency power options and transparent installation requirements."
          ]
        },
        {
          "heading": "5. Final checklist before ordering",
          "paragraphs": [
            "Confirm the door, dimensions, primary and backup access methods, app connectivity if needed and the installation plan before placing an order."
          ],
          "bullets": [
            "Door type and dimensions verified.",
            "Primary and backup access methods defined.",
            "Emergency power method understood.",
            "Correct model, price and variant confirmed.",
            "Door modification requirements agreed."
          ]
        }
      ],
      "conclusion": "If you are unsure which model fits, start with the apartment-lock collection and share door photos for compatibility screening."
    }
  },
  {
    "slug": "ket-sat-thong-minh-7-thong-so-can-kiem-tra",
    "category": "tu-van",
    "publishedAt": "2026-10-04",
    "updatedAt": "2026-10-04",
    "readMinutes": 7,
    "relatedPaths": [
      "/ket-sat",
      "/ket-sat-thong-minh-da-nang"
    ],
    "vi": {
      "title": "Két sắt thông minh: 7 thông số cần kiểm tra trước khi mua",
      "metaTitle": "Két sắt thông minh: 7 thông số cần kiểm tra | T Smart",
      "metaDescription": "7 thông số nên kiểm tra trước khi mua két sắt thông minh: kích thước, trọng lượng, khoang chứa, phương thức mở, nguồn điện, vị trí đặt và lối vận chuyển.",
      "excerpt": "Đừng chọn két chỉ theo chiều cao hoặc giá. Kích thước ngoài, khoang chứa, trọng lượng và lối vận chuyển có thể quyết định model có thực sự phù hợp hay không.",
      "intro": "Két sắt thông minh thường được so sánh bằng cân nặng, chiều cao và phương thức mở. Nhưng để tránh mua sai, cần nhìn đồng thời cả kích thước sử dụng, vị trí đặt và điều kiện vận chuyển.",
      "sections": [
        {
          "heading": "1. Kích thước ngoài",
          "paragraphs": [
            "Đo chiều cao, rộng và sâu của vị trí đặt trước. Nếu đặt trong hộc tủ, cần chừa khoảng cho tay nắm, bản lề và hành trình mở cửa."
          ]
        },
        {
          "heading": "2. Kích thước khoang sử dụng",
          "paragraphs": [
            "Hai két có kích thước ngoài gần giống nhau chưa chắc có dung tích sử dụng giống nhau. Cần xem khoang chính, ngăn phụ và cách bố trí bên trong có phù hợp giấy tờ, trang sức hoặc tài sản cần lưu trữ không."
          ]
        },
        {
          "heading": "3. Trọng lượng và tải trọng vị trí đặt",
          "paragraphs": [
            "Két nặng thường khó di chuyển hơn, nhưng trọng lượng cũng ảnh hưởng đến vận chuyển và vị trí đặt. Với tầng cao hoặc sàn có giới hạn tải, nên kiểm tra trước."
          ]
        },
        {
          "heading": "4. Phương thức mở khóa",
          "paragraphs": [
            "Vân tay, mã PIN, chìa cơ, ứng dụng hoặc phương thức xác thực kết hợp có ưu nhược điểm khác nhau. Hãy chọn theo tần suất sử dụng và số người cần truy cập."
          ]
        },
        {
          "heading": "5. Nguồn điện và phương án khẩn cấp",
          "paragraphs": [
            "Cần biết két dùng pin gì, cảnh báo pin yếu ra sao và cách cấp nguồn khẩn cấp khi pin hết. Đây là thông tin nên xác minh theo đúng model."
          ]
        },
        {
          "heading": "6. Lối vận chuyển",
          "paragraphs": [
            "Đo cửa ra vào, thang máy, cầu thang và các góc cua. Một chiếc két vừa vị trí đặt nhưng không đi qua được lối vận chuyển vẫn gây phát sinh lớn."
          ]
        },
        {
          "heading": "7. Hậu mãi và khả năng bảo trì",
          "paragraphs": [
            "Hãy kiểm tra cách xử lý khi quên mã, lỗi cảm biến hoặc cần thay linh kiện. Với thiết bị nặng, khả năng hỗ trợ tại chỗ quan trọng hơn nhiều sản phẩm điện tử nhỏ."
          ]
        }
      ],
      "conclusion": "Cách chọn két an toàn nhất là bắt đầu từ vị trí đặt và tài sản cần lưu trữ, sau đó mới so sánh công nghệ và giá."
    },
    "en": {
      "title": "Smart safes: 7 specifications to check before buying",
      "metaTitle": "Smart safes: 7 specifications to check | T Smart",
      "metaDescription": "Check these seven smart-safe factors before buying: dimensions, weight, usable storage, access methods, power, placement and delivery route.",
      "excerpt": "Do not choose a safe by height or price alone. External size, usable storage, weight and delivery access can determine whether it really fits.",
      "intro": "Smart safes are often compared by weight, height and unlocking methods. A better decision also considers usable space, placement and delivery constraints.",
      "sections": [
        {
          "heading": "1. External dimensions",
          "paragraphs": [
            "Measure the intended location first. Built-in placements need clearance for handles, hinges and the full door swing."
          ]
        },
        {
          "heading": "2. Usable interior space",
          "paragraphs": [
            "Similar external dimensions do not guarantee similar usable capacity. Check shelves, internal compartments and the items you plan to store."
          ]
        },
        {
          "heading": "3. Weight and floor conditions",
          "paragraphs": [
            "Weight affects both security handling and delivery. Upper floors and specific floor structures may need additional checks."
          ]
        },
        {
          "heading": "4. Access methods",
          "paragraphs": [
            "Fingerprint, PIN, mechanical key and app-based access serve different needs. Select around frequency of use and the number of authorised users."
          ]
        },
        {
          "heading": "5. Power and emergency access",
          "paragraphs": [
            "Confirm battery type, low-battery warnings and the emergency power or backup opening process for the exact model."
          ]
        },
        {
          "heading": "6. Delivery route",
          "paragraphs": [
            "Measure doors, lifts, stairs and tight corners. A safe that fits the room but cannot reach it is still the wrong choice."
          ]
        },
        {
          "heading": "7. Serviceability",
          "paragraphs": [
            "Understand support options for forgotten credentials, sensor faults and replacement parts, especially for heavy equipment that is difficult to move."
          ]
        }
      ],
      "conclusion": "Start with placement and storage needs, then compare technology and price."
    }
  },
  {
    "slug": "spotlight-va-downlight-khac-nhau-the-nao",
    "category": "so-sanh",
    "publishedAt": "2026-10-04",
    "updatedAt": "2026-10-04",
    "readMinutes": 6,
    "relatedPaths": [
      "/den-chieu-sang",
      "/giai-phap-chieu-sang-da-nang"
    ],
    "vi": {
      "title": "Spotlight và Downlight khác nhau thế nào? Chọn đúng cho từng không gian",
      "metaTitle": "Spotlight vs Downlight: khác nhau thế nào? | T Smart",
      "metaDescription": "So sánh Spotlight và Downlight theo góc chiếu, mục đích sử dụng, độ chói và cách bố trí cho phòng khách, showroom và không gian thương mại.",
      "excerpt": "Downlight thường đảm nhiệm ánh sáng nền, còn Spotlight tạo điểm nhấn. Nhưng để bố trí đẹp, cần xem thêm góc chiếu, khoảng cách và bề mặt cần chiếu.",
      "intro": "Spotlight và Downlight có thể dùng cùng một không gian nhưng đảm nhiệm hai vai trò khác nhau. Việc phân biệt đúng giúp tránh tình trạng phòng đủ sáng nhưng phẳng, hoặc có nhiều điểm nhấn nhưng gây chói.",
      "sections": [
        {
          "heading": "1. Downlight: ưu tiên ánh sáng nền",
          "paragraphs": [
            "Downlight thường được dùng để tạo lớp sáng chung tương đối đều. Khi chọn, nên xem công suất, quang thông, nhiệt màu, CRI và lỗ khoét trần.",
            "Khoảng cách giữa các đèn cần liên quan đến chiều cao trần và vùng phủ sáng, không nên bố trí chỉ theo hình học của trần."
          ]
        },
        {
          "heading": "2. Spotlight: ưu tiên điểm nhấn",
          "paragraphs": [
            "Spotlight có chùm sáng tập trung hơn, phù hợp chiếu tranh, vật liệu, sản phẩm trưng bày hoặc tạo nhịp sáng tối.",
            "Góc chiếu càng hẹp thì vùng sáng càng tập trung, vì vậy vị trí lắp và khoảng cách đến vật thể cần được tính trước."
          ]
        },
        {
          "heading": "3. Phòng khách nên phối thế nào?",
          "paragraphs": [
            "Có thể dùng Downlight cho nền và Spotlight cho tường, tranh hoặc vật liệu cần nhấn. Cách phối này giúp không gian có chiều sâu hơn so với chỉ dùng một loại đèn."
          ]
        },
        {
          "heading": "4. Showroom cần lưu ý CRI và độ chói",
          "paragraphs": [
            "Showroom cần thể hiện màu vật liệu và sản phẩm đúng hơn, nên CRI, góc chiếu và vị trí đèn quan trọng. Đèn quá mạnh hoặc đặt sai góc có thể gây chói và tạo bóng không mong muốn."
          ]
        },
        {
          "heading": "5. Chọn theo bản vẽ thay vì chọn từng chiếc",
          "paragraphs": [
            "Với công trình có nhiều khu vực, nên xác định lớp ánh sáng và vị trí trước, sau đó mới chốt model. Điều này giúp kiểm soát số lượng, công suất và cảm giác tổng thể tốt hơn."
          ]
        }
      ],
      "conclusion": "Downlight và Spotlight không thay thế hoàn toàn cho nhau. Phương án tốt thường là phối hợp hai lớp ánh sáng theo đúng mục tiêu của từng khu vực."
    },
    "en": {
      "title": "Spotlight vs downlight: how are they different?",
      "metaTitle": "Spotlight vs Downlight: key differences | T Smart",
      "metaDescription": "Compare spotlights and downlights by beam angle, purpose, glare and placement for homes, showrooms and commercial spaces.",
      "excerpt": "Downlights usually provide ambient light while spotlights create accents. Beam angle, spacing and target surfaces still matter.",
      "intro": "Spotlights and downlights can share the same room, but they play different roles. Understanding the difference helps avoid flat lighting or excessive glare.",
      "sections": [
        {
          "heading": "1. Downlights for ambient layers",
          "paragraphs": [
            "Downlights are commonly used for relatively even general illumination. Compare wattage, output, CCT, CRI and ceiling cut-out size.",
            "Spacing should reflect ceiling height and coverage rather than ceiling geometry alone."
          ]
        },
        {
          "heading": "2. Spotlights for accents",
          "paragraphs": [
            "Spotlights focus light on art, materials, displays or architectural features. Narrower beams create more concentrated pools of light."
          ]
        },
        {
          "heading": "3. Living-room combinations",
          "paragraphs": [
            "A practical approach is to use downlights for the base layer and spotlights for walls, artwork or key materials."
          ]
        },
        {
          "heading": "4. Showrooms need CRI and glare control",
          "paragraphs": [
            "Colour rendering, beam angle and aiming matter more in display environments. Poor positioning can create glare or unwanted shadows."
          ]
        },
        {
          "heading": "5. Plan the layout before the fixture",
          "paragraphs": [
            "For multi-zone projects, define lighting layers and positions first, then select fixtures and power levels."
          ]
        }
      ],
      "conclusion": "Downlights and spotlights are complementary rather than interchangeable. The strongest layouts use each for its intended role."
    }
  },
  {
    "slug": "motor-cong-am-san-va-tay-don-nen-chon-loai-nao",
    "category": "so-sanh",
    "publishedAt": "2026-10-04",
    "updatedAt": "2026-10-04",
    "readMinutes": 6,
    "relatedPaths": [
      "/cong-tu-dong",
      "/cong-tu-dong-da-nang"
    ],
    "vi": {
      "title": "Motor cổng âm sàn và tay đòn: nên chọn loại nào?",
      "metaTitle": "Motor cổng âm sàn vs tay đòn | T Smart",
      "metaDescription": "So sánh motor cổng âm sàn và motor tay đòn theo thẩm mỹ, điều kiện thi công, bảo trì, tải trọng và không gian lắp đặt.",
      "excerpt": "Âm sàn gọn về thẩm mỹ, tay đòn dễ tiếp cận hơn. Lựa chọn đúng phụ thuộc vào kết cấu cổng, nền, tải trọng và khả năng bảo trì.",
      "intro": "Motor âm sàn và motor tay đòn đều phù hợp với cổng mở cánh, nhưng yêu cầu thi công và bảo trì khác nhau. Chọn theo hình thức alone dễ dẫn đến cấu hình không phù hợp với hiện trạng.",
      "sections": [
        {
          "heading": "1. Motor âm sàn: ưu tiên thẩm mỹ",
          "paragraphs": [
            "Cơ cấu motor được bố trí dưới nền nên phần nhìn thấy ít hơn. Phương án này phù hợp khi muốn giữ thiết kế cổng gọn và hạn chế tay motor lộ ra ngoài.",
            "Đổi lại, cần kiểm tra vị trí hố motor, thoát nước, nền và khả năng tiếp cận khi bảo trì."
          ]
        },
        {
          "heading": "2. Motor tay đòn: dễ thi công và bảo trì",
          "paragraphs": [
            "Motor tay đòn nằm trên cánh hoặc trụ nên dễ tiếp cận hơn. Với nhiều công trình cải tạo, đây là phương án thuận tiện nếu còn đủ không gian hình học cho tay đòn vận hành."
          ]
        },
        {
          "heading": "3. Không chỉ nhìn tải trọng",
          "paragraphs": [
            "Trọng lượng cánh là dữ liệu quan trọng nhưng chưa đủ. Chiều dài cánh, bản lề, ma sát, góc mở và tần suất sử dụng đều ảnh hưởng đến lựa chọn motor."
          ]
        },
        {
          "heading": "4. An toàn và nguồn dự phòng",
          "paragraphs": [
            "Nên tính cả photocell, cảm biến an toàn, mở tay khi mất điện và nguồn dự phòng nếu công trình cần. Bộ điều khiển và phụ kiện là một phần của hệ thống, không phải hạng mục tách rời."
          ]
        },
        {
          "heading": "5. Chọn loại nào?",
          "paragraphs": [
            "Ưu tiên âm sàn khi điều kiện nền phù hợp và thẩm mỹ là mục tiêu lớn. Ưu tiên tay đòn khi muốn dễ tiếp cận, dễ bảo trì và hiện trạng cổng cho phép lắp cơ cấu tay."
          ]
        }
      ],
      "conclusion": "Trước khi chốt motor, hãy đo chiều dài cánh, ước lượng tải trọng, kiểm tra bản lề và không gian lắp. Một khảo sát ngắn có thể tránh chọn sai cơ cấu."
    },
    "en": {
      "title": "Underground vs swing-arm gate motors: which should you choose?",
      "metaTitle": "Underground vs swing-arm gate motors | T Smart",
      "metaDescription": "Compare underground and swing-arm gate motors by aesthetics, installation conditions, maintenance, gate load and available space.",
      "excerpt": "Underground motors hide the mechanism; swing arms are easier to access. The right choice depends on gate structure, ground conditions and maintenance needs.",
      "intro": "Both underground and swing-arm motors can automate swing gates, but their installation and maintenance requirements differ. Appearance alone should not decide the system.",
      "sections": [
        {
          "heading": "1. Underground motors prioritise appearance",
          "paragraphs": [
            "The drive mechanism sits below ground, reducing visible hardware. This suits projects where a clean gate design matters.",
            "Ground conditions, drainage and future service access need to be planned carefully."
          ]
        },
        {
          "heading": "2. Swing-arm motors are accessible",
          "paragraphs": [
            "Swing-arm motors remain visible but are generally easier to reach for installation and maintenance when the gate geometry allows them."
          ]
        },
        {
          "heading": "3. Load is not the only parameter",
          "paragraphs": [
            "Gate weight matters, but leaf length, hinges, friction, opening angle and duty cycle also affect motor selection."
          ]
        },
        {
          "heading": "4. Safety and backup power",
          "paragraphs": [
            "Photocells, safety sensing, manual release and backup power should be considered with the controller as one complete system."
          ]
        },
        {
          "heading": "5. Which type fits?",
          "paragraphs": [
            "Choose underground when site conditions support it and visual cleanliness is a priority. Choose swing-arm when access, maintenance and straightforward installation matter more."
          ]
        }
      ],
      "conclusion": "Measure leaf length, estimate weight and inspect hinges and installation space before choosing the mechanism."
    }
  },
  {
    "slug": "checklist-khao-sat-chieu-sang-showroom-da-nang",
    "category": "du-an-thi-cong",
    "publishedAt": "2026-10-04",
    "updatedAt": "2026-10-04",
    "readMinutes": 7,
    "relatedPaths": [
      "/giai-phap-chieu-sang-da-nang",
      "/den-chieu-sang"
    ],
    "vi": {
      "title": "Checklist khảo sát chiếu sáng showroom tại Đà Nẵng trước khi thi công",
      "metaTitle": "Checklist khảo sát chiếu sáng showroom Đà Nẵng | T Smart",
      "metaDescription": "Checklist khảo sát chiếu sáng showroom tại Đà Nẵng: mặt bằng, cao độ trần, vật liệu, CRI, góc chiếu, mạch điện và khu vực cần tạo điểm nhấn.",
      "excerpt": "Trước khi chọn đèn cho showroom, nên kiểm tra mặt bằng, trần, vật liệu, vị trí trưng bày và yêu cầu màu sắc. Đây là checklist dùng cho bước khảo sát trước thi công.",
      "intro": "Bài này là checklist khảo sát dự án, không phải một case study của công trình cụ thể. Mục tiêu là giúp chủ showroom và đơn vị thi công thống nhất dữ liệu trước khi chọn đèn và bố trí mạch.",
      "sections": [
        {
          "heading": "1. Ghi lại mặt bằng và cao độ trần",
          "paragraphs": [
            "Kích thước khu vực, chiều cao trần và vị trí dầm ảnh hưởng trực tiếp đến khoảng cách đèn và góc chiếu. Nên có bản vẽ hoặc tối thiểu bản phác thảo có kích thước."
          ]
        },
        {
          "heading": "2. Xác định vật thể cần nhấn",
          "paragraphs": [
            "Showroom không nên chiếu sáng đều mọi điểm như nhau. Hãy đánh dấu khu trưng bày chính, logo, vật liệu, kệ và lối đi để xác định lớp sáng nền và lớp nhấn."
          ]
        },
        {
          "heading": "3. Kiểm tra màu vật liệu và yêu cầu CRI",
          "paragraphs": [
            "Sản phẩm có màu sắc nhạy như nội thất, thời trang hoặc vật liệu bề mặt cần lưu ý CRI và nhiệt màu. Việc này nên thống nhất trước khi đặt toàn bộ đèn."
          ]
        },
        {
          "heading": "4. Kiểm tra hệ ray, lỗ khoét và nguồn",
          "paragraphs": [
            "Với đèn ray, cần thống nhất vị trí ray và khả năng thay đổi đầu đèn. Với đèn âm trần, phải chốt kích thước lỗ khoét và khoảng trống phía trên trần."
          ]
        },
        {
          "heading": "5. Chia mạch điều khiển",
          "paragraphs": [
            "Nên tách nhóm đèn theo khu vực hoặc mục đích để có thể bật tắt linh hoạt. Nếu cần dimming hoặc điều khiển thông minh, phương án dây và driver nên được xác định từ đầu."
          ]
        },
        {
          "heading": "6. Kiểm tra điểm nhìn và chống chói",
          "paragraphs": [
            "Đứng ở các vị trí khách thường di chuyển để kiểm tra nguy cơ nhìn trực tiếp vào nguồn sáng. Góc chiếu nên làm nổi sản phẩm mà không gây khó chịu cho người xem."
          ]
        }
      ],
      "conclusion": "Một buổi khảo sát tốt giúp giảm việc thay đổi đèn sau khi hoàn thiện. Với showroom, nên chốt logic ánh sáng trước khi chốt số lượng sản phẩm."
    },
    "en": {
      "title": "Pre-install lighting survey checklist for showrooms in Da Nang",
      "metaTitle": "Showroom lighting survey checklist in Da Nang | T Smart",
      "metaDescription": "A showroom lighting survey checklist covering floor plan, ceiling height, materials, CRI, beam angle, circuits and accent zones.",
      "excerpt": "Before choosing showroom fixtures, review the plan, ceiling, materials, displays and colour requirements. This checklist supports the pre-install survey.",
      "intro": "This is a project survey checklist rather than a case study of a specific completed site. It helps owners and installers agree on the data needed before fixture selection.",
      "sections": [
        {
          "heading": "1. Record the floor plan and ceiling height",
          "paragraphs": [
            "Room dimensions, ceiling height and beams directly affect spacing and aiming. Use a measured drawing or at least a dimensioned sketch."
          ]
        },
        {
          "heading": "2. Mark the objects that need emphasis",
          "paragraphs": [
            "A showroom should not illuminate every point equally. Identify primary displays, logos, materials, shelving and circulation routes."
          ]
        },
        {
          "heading": "3. Review materials and CRI requirements",
          "paragraphs": [
            "Colour-sensitive products such as interiors, fashion and finishes benefit from deliberate CRI and colour-temperature choices."
          ]
        },
        {
          "heading": "4. Check tracks, cut-outs and power",
          "paragraphs": [
            "Track lighting needs defined track positions; recessed lighting needs cut-out sizes and ceiling void clearance."
          ]
        },
        {
          "heading": "5. Divide control circuits",
          "paragraphs": [
            "Separate lighting by area or purpose so scenes can be adjusted. Dimming or smart control should be planned with drivers and wiring from the beginning."
          ]
        },
        {
          "heading": "6. Check viewing angles and glare",
          "paragraphs": [
            "Stand where visitors usually move and look for direct glare. Aim the light at products without making the source uncomfortable to view."
          ]
        }
      ],
      "conclusion": "A good survey reduces fixture changes after completion. Establish the lighting logic before finalising quantities."
    }
  },
  {
    "slug": "face-id-van-tay-tinh-mach-khac-nhau-gi",
    "category": "cong-nghe",
    "publishedAt": "2026-10-04",
    "updatedAt": "2026-10-04",
    "readMinutes": 7,
    "relatedPaths": [
      "/khoa-thong-minh",
      "/khoa-thong-minh-da-nang"
    ],
    "vi": {
      "title": "Face ID, vân tay và tĩnh mạch khác nhau gì trên khóa thông minh?",
      "metaTitle": "Face ID, vân tay, tĩnh mạch trên khóa thông minh | T Smart",
      "metaDescription": "Giải thích khác biệt giữa Face ID, vân tay và nhận diện tĩnh mạch trên khóa thông minh, ưu nhược điểm và cách chọn theo nhu cầu sử dụng.",
      "excerpt": "Ba công nghệ sinh trắc học đều nhằm xác thực người dùng nhưng cách tương tác và điều kiện sử dụng khác nhau. Không có công nghệ nào phù hợp tuyệt đối cho mọi gia đình.",
      "intro": "Khóa thông minh ngày càng có nhiều phương thức sinh trắc học. Để chọn đúng, nên hiểu cách người dùng tương tác với từng công nghệ thay vì chỉ nhìn số lượng tính năng trên bảng thông số.",
      "sections": [
        {
          "heading": "1. Vân tay: trực tiếp và quen thuộc",
          "paragraphs": [
            "Người dùng chạm ngón tay vào cảm biến để xác thực. Điểm mạnh là thao tác quen và nhanh trên nhiều dòng khóa.",
            "Trải nghiệm có thể khác nhau tùy chất lượng cảm biến, vị trí đặt và tình trạng vân tay của từng người."
          ]
        },
        {
          "heading": "2. Face ID: mở cửa không chạm",
          "paragraphs": [
            "Nhận diện khuôn mặt giúp người dùng không cần đưa tay vào cảm biến. Điều này hữu ích khi mang đồ hoặc muốn thao tác nhanh.",
            "Cần quan tâm góc nhận diện, chiều cao lắp và môi trường thực tế của cửa."
          ]
        },
        {
          "heading": "3. Tĩnh mạch: thêm một lựa chọn sinh trắc học",
          "paragraphs": [
            "Nhận diện tĩnh mạch lòng bàn tay hoặc ngón tay sử dụng đặc trưng mạch máu để xác thực. Trên thị trường, cách triển khai và vùng nhận diện khác nhau theo model.",
            "Nên thử thao tác thực tế và kiểm tra tài liệu của đúng sản phẩm thay vì suy luận từ tên công nghệ."
          ]
        },
        {
          "heading": "4. Không nên bỏ qua PIN, thẻ và chìa dự phòng",
          "paragraphs": [
            "Sinh trắc học thuận tiện nhưng phương thức dự phòng vẫn quan trọng. Gia đình nên hiểu cách dùng PIN, thẻ, chìa cơ hoặc nguồn khẩn cấp nếu model có hỗ trợ."
          ]
        },
        {
          "heading": "5. Chọn công nghệ sau khi xác minh cửa",
          "paragraphs": [
            "Dù công nghệ nào, điều kiện đầu tiên vẫn là khóa phải tương thích với cửa. Loại cửa, độ dày, đố cửa và thân khóa cần được kiểm tra trước khi chọn tính năng."
          ]
        }
      ],
      "conclusion": "Hãy xem Face ID, vân tay và tĩnh mạch như các phương thức sử dụng khác nhau. Model tốt nhất là model vừa phù hợp cửa, vừa phù hợp thói quen của người dùng."
    },
    "en": {
      "title": "Face ID, fingerprint and vein recognition on smart locks",
      "metaTitle": "Face ID, fingerprint and vein recognition | T Smart",
      "metaDescription": "Understand Face ID, fingerprint and vein recognition on smart locks, including practical differences and how to choose for daily use.",
      "excerpt": "All three biometric methods authenticate users, but interaction and installation conditions differ. No single technology is ideal for every household.",
      "intro": "Smart locks increasingly combine several biometric methods. A better choice comes from understanding how each method is used rather than counting features.",
      "sections": [
        {
          "heading": "1. Fingerprint: direct and familiar",
          "paragraphs": [
            "Users touch a sensor to authenticate. The interaction is familiar and fast across many lock ranges.",
            "Experience can vary with sensor quality, placement and the user's fingerprint condition."
          ]
        },
        {
          "heading": "2. Face ID: touch-free access",
          "paragraphs": [
            "Facial recognition avoids touching the lock and can be convenient when carrying items.",
            "Viewing angle, installation height and the actual doorway environment still matter."
          ]
        },
        {
          "heading": "3. Vein recognition: another biometric option",
          "paragraphs": [
            "Palm- or finger-vein systems use vascular patterns for authentication. Implementations and sensing areas vary by model.",
            "Test the real interaction and review the exact product documentation rather than judging by the technology name alone."
          ]
        },
        {
          "heading": "4. Keep backup methods in mind",
          "paragraphs": [
            "Biometrics are convenient, but PIN, card, mechanical key or emergency power can still be important backup options where supported."
          ]
        },
        {
          "heading": "5. Verify the door before choosing technology",
          "paragraphs": [
            "Regardless of biometric method, the lock must fit the door. Door type, thickness, stile width and mortise should be checked first."
          ]
        }
      ],
      "conclusion": "Treat Face ID, fingerprint and vein recognition as different interaction methods. The best model is the one that fits both the door and the household."
    }
  }
];

export const newsArticleBySlug = new Map(
  newsArticles.map((article) => [article.slug, article]),
);

export const newsPaths = [
  "/tin-tuc",
  "/en/tin-tuc",
  ...newsArticles.flatMap((article) => [
    `/tin-tuc/${article.slug}`,
    `/en/tin-tuc/${article.slug}`,
  ]),
];

export function newsCategoryLabel(
  category: NewsCategoryId,
  locale: Locale,
) {
  const item = newsCategories.find((entry) => entry.id === category);
  return item ? item[locale] : category;
}
